import React, { createContext, useContext, useState } from 'react';

const ErrorContext = createContext();

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

export const ErrorProvider = ({ children }) => {
  const [error, setError] = useState(null);

  const showError = (message) => {
    setError(message);
  };

  const clearError = () => {
    setError(null);
  };

  const handleGraphQLError = (error) => {
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      showError(error.graphQLErrors[0].message);
    } else if (error.networkError) {
      let message = 'Network error - please try again';
      
      // Try to extract more specific error info
      if (error.networkError.result?.errors?.[0]?.message) {
        message = error.networkError.result.errors[0].message;
      } else if (error.networkError.bodyText) {
        try {
          const parsed = JSON.parse(error.networkError.bodyText);
          if (parsed.errors?.[0]?.message) {
            message = parsed.errors[0].message;
          } else if (parsed.error) {
            message = parsed.error;
          }
        } catch (e) {
          // If we can't parse the body, check for other error info
          if (error.networkError.statusCode) {
            message = `Server error (${error.networkError.statusCode})`;
            if (error.networkError.message && error.networkError.message !== 'Network error') {
              message += `: ${error.networkError.message}`;
            }
          }
        }
      } else if (error.networkError.message && error.networkError.message !== 'Network error') {
        message = error.networkError.message;
      }
      
      showError(message);
    } else {
      showError('An unexpected error occurred');
    }
  };

  const handleMutationResult = (result) => {
    // Check for errors in mutation result
    const mutationKey = Object.keys(result.data || {})[0];
    const mutationData = result.data?.[mutationKey];
    
    if (mutationData?.errors && mutationData.errors.length > 0) {
      showError(mutationData.errors[0]);
      return false; // Indicates error
    }
    
    return true; // Indicates success
  };

  return (
    <ErrorContext.Provider value={{
      error,
      showError,
      clearError,
      handleGraphQLError,
      handleMutationResult
    }}>
      {children}
      
      {/* Global Error Dialog */}
      {error && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full mx-4">
            <h3 className="text-lg font-bold mb-4 text-red-600 text-center">Error</h3>
            <p className="text-gray-700 mb-4 text-center">{error}</p>
            <button
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
              onClick={clearError}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </ErrorContext.Provider>
  );
};